import classNames from 'classnames';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Popup from 'reactjs-popup';
import { ReactComponent as CloseIcon } from '../../assets/images/close-button.svg';
import styles from './modals.module.scss';

interface ISimpleDismissableModalProps {
  trigger: JSX.Element;
  children: string | JSX.Element | ((close: () => void) => JSX.Element);
  onOpen?(): void;
  onClose?(): void;
  closeIconClassName?: string;
  containerClassName?: string;
}

export default function SimpleDismissableModal({
  trigger,
  children,
  onOpen,
  onClose,
  closeIconClassName,
  containerClassName,
}: ISimpleDismissableModalProps) {
  const { t } = useTranslation();
  const bodyRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  // Ref callback (not a plain ref) because lazy-loaded modal content can
  // suspend on first open, remounting the popup's dialog element and wiping
  // any attribute set only in onOpen. This re-marks the dialog every mount.
  const attachCloseButton = (node: HTMLButtonElement | null) => {
    closeButtonRef.current = node;
    node?.closest('[role="dialog"]')?.setAttribute('aria-modal', 'true');
  };

  // Use static class name for Popup instead of css module because that component munges the class name internally
  return (
    <Popup
      className="s3-popup"
      trigger={trigger}
      onOpen={() => {
        if (onOpen) {
          onOpen();
        }

        // Hack (nick): When the modal is opened, if the body content is long enough to have overflow, it's
        // automatically scrolled to the bottom. I have no idea why. This hack scrolls back to the top.
        if (bodyRef.current) {
          bodyRef.current.scrollTop = 0;
        }

        // reactjs-popup only moves focus into the dialog if it finds a focusable element at open
        // time, which fails when the content is still lazy-loading (a spinner has none). The close
        // button always exists, so focus it explicitly.
        closeButtonRef.current?.focus();
      }}
      onClose={onClose}
      modal
      closeOnDocumentClick={true}
    >
      {(close: () => void) => (
        <div className={classNames(styles.container, styles.dismissable, containerClassName)}>
          <button
            type="button"
            ref={attachCloseButton}
            aria-label={t('close')}
            className={classNames('s3-popup-close-icon', styles.closeIcon, closeIconClassName)}
            onClick={close}
          >
            <CloseIcon />
          </button>
          <div className={classNames('s3-popup-body', styles.body)} ref={bodyRef}>
            {typeof children === 'function'
              ? children(close)
              : React.isValidElement(children)
                ? React.cloneElement(children as React.ReactElement<any>, { close })
                : children}
          </div>
        </div>
      )}
    </Popup>
  );
}
